// Shared domain types between web and api

export type CreativeKind =
  | "image"
  | "video"
  | "avatar"
  | "playable"
  | "aigc_panel"
  | "aigc_influencer"
  | "aigc_podcast"
  | "aigc_doctor"
  | "aigc_twitter"
  | "aigc_custom"
  | "compose_split"
  | "compose_takeover"
  | "compose_rembg"
  | "motioned";

export type AspectRatio = "1:1" | "9:16" | "4:5" | "16:9";

export const SIZE_CODE: Record<AspectRatio, "k" | "s" | "f" | "l"> = {
  "1:1": "k",
  "9:16": "s",
  "4:5": "f",
  "16:9": "l",
};

export type Language =
  | "EN" | "TR" | "DE" | "FR" | "ES" | "IT" | "PT" | "PT-BR"
  | "AR" | "RU" | "NL" | "PL" | "SV" | "NO" | "JA" | "KO" | "ZH";

export const LANG_CODE: Record<Language, string> = {
  EN: "en", TR: "tr", DE: "de", FR: "fr", ES: "es",
  IT: "it", PT: "pt", "PT-BR": "ptbr", AR: "ar", RU: "ru",
  NL: "nl", PL: "pl", SV: "sv", NO: "no", JA: "ja", KO: "ko", ZH: "zh",
};

export type Theme = "light" | "dark";

export type CreativeType =
  | "List"
  | "Pyramid"
  | "Illustration"
  | "Split"
  | "Optical Illusion"
  | "Spectrum";

export const ALL_TYPES: CreativeType[] = [
  "List",
  "Pyramid",
  "Illustration",
  "Split",
  "Optical Illusion",
  "Spectrum",
];

// API DTOs

export interface Product {
  id: string;
  name: string;
  tag: string;
  drive_folder_id: string;
  created_at: string;
  angles?: string[];      // legacy alias for pain_points
  pain_points?: string[]; // configured pain point names
  audiences?: string[];   // configured audience options
}

export interface Term {
  id: string;
  product_id: string;
  name: string;
}

export interface Stage {
  id: string;
  name: string;
}

export interface JobCreateRequest {
  product_id: string;
  term_id?: string;      // legacy — omit when using feature_id
  feature_id?: string;   // replaces term_id; term is auto-resolved from feature name
  types: CreativeType[];
  themes: Theme[];
  stages?: ("Unaware" | "Aware")[];
  languages: Language[];
  count: number; // angle count (used when angle_names not provided)
  angle_names?: string[]; // specific named angles (overrides count)
  audience?: string; // target audience context for text generation
  pain_point?: string; // selected pain point — seed for AI angle generation; stored in genome
  sizes: AspectRatio[];
  kind?: CreativeKind; // default "image"
  text_provider?: "claude" | "gemini";
  image_provider?: "fal-nano-banana-2" | "fal-gpt-image-2";
  video_duration?: 5 | 10; // only for kind === "video"
  campaign_avatar?: {
    compose_formats: string[]; // formats to auto-compose after avatar completes
  };
  campaign_id?: string; // if set, completed files are copied to campaign's Drive batch folder
}

export interface Job {
  id: string;
  product_id: string;
  term_id: string;
  status: "queued" | "running" | "completed" | "failed";
  params_json: string;
  cost_usd: number;
  error?: string | null;
  created_at: string;
  finished_at?: string | null;
}

export interface Media {
  id: string;
  job_id: string;
  product_id: string;
  code: string; // padded counter, e.g. "00001"
  angle: string;
  stage: string;
  theme: Theme;
  type: CreativeType;
  hook: string;
  body: string;
  cta: string;
  drive_folder: string;
}

export interface MediaFile {
  id: string;
  media_id: string;
  language: Language;
  size: AspectRatio;
  drive_file_id: string;
  file_name: string;
  cost_usd: number;
}

export interface CostRow {
  id: string;
  job_id: string;
  provider: string; // "claude" | "gemini" | "fal-ai" | ...
  kind: string; // "text" | "image" | ...
  units: number;
  usd: number;
  meta_json?: string;
}

export function buildFileName(opts: {
  tag: string;
  code: string;
  kind: CreativeKind;
  lang: string;
  size: AspectRatio;
  term: string;
  ddmmyyyy: string;
}): string {
  const sizeCode = SIZE_CODE[opts.size];
  const term = opts.term.toLowerCase().replace(/\s+/g, "");
  return `${opts.tag}_${opts.code}_${opts.kind}_${opts.lang}_${sizeCode}_${term}_${opts.ddmmyyyy}`;
}
